import type {AppError, Result} from '@packages/utils';
import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  CompaniesSchema,
  DepartmentsSchema,
  ReportSummarySchema,
  type SummaryContent,
  type SummaryContentAdvice,
  type SummaryContentGeneral,
  type SummaryContentQuestion,
  type SummaryContentStep,
  type SummaryContentThemaTexts,
} from '../entities/reportAnalytics/schema';

export type reportSummary = z.infer<typeof ReportSummarySchema>;
export type summaryContent = z.infer<typeof SummaryContent>;
export type parseReportSummary = {
  typeID: number;
  typeName: string;
  summary: summaryContent | undefined;
  reference: number[] | never[];
};
export type summaryContentStep = z.infer<typeof SummaryContentStep>;
export type summaryContentQuestion = z.infer<typeof SummaryContentQuestion>;
export type summaryContentAdvice = z.infer<typeof SummaryContentAdvice>;
export type summaryContentGeneral = z.infer<typeof SummaryContentGeneral>;
export type summaryContentThemaTexts = z.infer<typeof SummaryContentThemaTexts>;

export type GetReportSummaryResponse = z.infer<typeof ReportSummarySchema>;
export type ResultGetReportSummary = Result<GetReportSummaryResponse, AppError>;
export type GetDepartmentsInSummaryResponse = z.infer<typeof DepartmentsSchema>;
export type GetCompaniesInSummaryResponse = z.infer<typeof CompaniesSchema>;

type GetReportSummaryArgs = {
  version?: string;
  companyID: number;
  departmentID: number;
  queryParams: {
    reportType?: string;
  };
};

export const getReportSummary = async ({
  version = DEFAULT_API_VERSION,
  companyID,
  departmentID,
  queryParams,
}: GetReportSummaryArgs): Promise<ResultGetReportSummary> => {
  const path = createPathWithQuery(
    `/${version}/reports/summary/${companyID}/${departmentID}`,
    {...queryParams},
    'none',
  );
  const result = await defaultClient.get(path, ReportSummarySchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type GetReportSummaryOptions = {
  config?: UseQueryOptions<ResultGetReportSummary>;
} & GetReportSummaryArgs;

export const useGetReportSummary = ({
  companyID,
  departmentID,
  queryParams,
  config,
}: GetReportSummaryOptions) => {
  return useQuery<ResultGetReportSummary>({
    queryKey: [
      'reports',
      'summary',
      companyID,
      departmentID,
      queryParams.reportType,
    ],
    queryFn: async () =>
      getReportSummary({companyID, departmentID, queryParams}),
    ...config,
  });
};

type GetDepartmentsInSummaryArgs = {
  version?: string;
  companyID: number;
  queryParams: {
    reportType?: string;
  };
};

export const getDepartmentsInSummary = async ({
  version = DEFAULT_API_VERSION,
  companyID,
  queryParams,
}: GetDepartmentsInSummaryArgs) => {
  const path = createPathWithQuery(
    `/${version}/reports/summary/${companyID}/departments`,
    {...queryParams},
    'none',
  );
  const result = await defaultClient.get(path, DepartmentsSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type GetDepartmentsInSummaryOptions = {
  config?: UseQueryOptions<Result<GetDepartmentsInSummaryResponse, AppError>>;
} & GetDepartmentsInSummaryArgs;

export const useGetDepartmentsInSummary = ({
  companyID,
  queryParams,
  config,
}: GetDepartmentsInSummaryOptions) => {
  return useQuery<Result<GetDepartmentsInSummaryResponse, AppError>>({
    queryKey: [
      'reports',
      'summary',
      'departments',
      companyID,
      queryParams.reportType,
    ],
    queryFn: async () => getDepartmentsInSummary({companyID, queryParams}),
    ...config,
  });
};

type GetCompaniesInSummaryArgs = {
  version?: string;
  queryParams: {
    reportType?: string;
  };
};

export const getCompaniesInSummary = async ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetCompaniesInSummaryArgs) => {
  const path = createPathWithQuery(
    `/${version}/reports/summary/companies`,
    {...queryParams},
    'none',
  );
  const result = await defaultClient.get(path, CompaniesSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type GetCompaniesInSummaryOptions = {
  config?: UseQueryOptions<Result<GetCompaniesInSummaryResponse, AppError>>;
} & GetCompaniesInSummaryArgs;

export const useGetCompaniesInSummary = ({
  queryParams,
  config,
}: GetCompaniesInSummaryOptions) => {
  return useQuery<Result<GetCompaniesInSummaryResponse, AppError>>({
    queryKey: ['reports', 'summary', 'companies', queryParams.reportType],
    queryFn: async () => getCompaniesInSummary({queryParams}),
    ...config,
  });
};
